 
"use client";

import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CornerDownRight,
  HelpCircle,
  Loader2,
  Mic,
  Move,
  RotateCcw,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";

import {
  ChatThreadPanel,
  type ChatThreadPanelHandle,
} from "@/components/scirix/chat/chat-thread-panel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type {
  TaskPulseEntry,
  TaskPulseEntryKind,
  TaskPulseEvent,
  TaskThreadEvent,
  UserCommentEvent,
} from "@/types/task-thread";

import type {
  InstantAssistantContextCard,
  InstantAssistantContextSummary,
  InstantAssistantMessage,
  InstantAssistantQueueState,
  InstantAssistantStatusEvent,
  InstantAssistantToolCallEvent,
  InstantAssistantVoiceSessionResponse,
} from "@/types/assistant";
import { useSession } from "@/hooks/useSession";

import { useInstantAssistantController } from "../useInstantAssistantController";
import { useInstantAssistantStore } from "../useInstantAssistantStore";
import type { VoiceConnectionState } from "../useInstantAssistantStore";
import { useAssistantPrompts } from "../prompt-hooks";
import { useAssistantContextSource } from "../context/useAssistantContextSource";
import { loadVoiceOptIn, persistVoiceOptIn } from "../voice/voice-preferences";

const MAX_COMPOSER_PROMPT_REPEATS = 4;

type PanelBounds = {
  width: number;
  height: number;
  x: number;
  y: number;
};

type PanelInteraction =
  | { type: "move"; offsetX: number; offsetY: number }
  | { type: "resize"; startWidth: number; startHeight: number; startX: number; startY: number };

const PANEL_STORAGE_KEY = "instant-assistant:panel-bounds";
const PANEL_MARGIN = 16;
const PANEL_DEFAULT_WIDTH = 640;
const PANEL_DEFAULT_HEIGHT = 560;
const PANEL_MIN_WIDTH = 420;
const PANEL_MIN_HEIGHT = 360;

export function InstantAssistantPanel() {
  const { session } = useSession();
  const {
    featureEnabled,
    voiceFeatureEnabled,
    isPanelOpen,
    messages,
    toolCalls,
    queue,
    composerDraft,
    isSending,
    errorMessage,
    namespaceId,
    discoveryUi,
    focusReleaseSignal,
    contextCards,
    status,
    hasSelection,
    voiceSession,
    isVoiceConnecting,
    voiceConnectionState,
    voiceError,
    voiceLevel,
    voiceStartedAt,
    voiceMuted,
  } = useInstantAssistantStore((state) => ({
    featureEnabled: state.featureEnabled,
    voiceFeatureEnabled: state.voiceFeatureEnabled,
    isPanelOpen: state.isPanelOpen,
    messages: state.messages,
    toolCalls: state.toolCalls,
    queue: state.queue,
    composerDraft: state.composerDraft,
    isSending: state.isSending,
    errorMessage: state.errorMessage,
    namespaceId: state.namespaceId,
    discoveryUi: state.discoveryUi,
    focusReleaseSignal: state.focusReleaseSignal,
    contextCards: state.contextCards,
    status: state.status,
    hasSelection: state.hasSelection,
    voiceSession: state.voiceSession,
    isVoiceConnecting: state.isVoiceConnecting,
    voiceConnectionState: state.voiceConnectionState,
    voiceError: state.voiceError,
    voiceLevel: state.voiceLevel,
    voiceStartedAt: state.voiceStartedAt,
    voiceMuted: state.voiceMuted,
  }));
  const isAuthenticated = session.status === "authenticated";
  const actions = useInstantAssistantStore((state) => state.actions);
  const { sendPrompt, submitFeedback, startVoiceSession, endVoiceSession, setVoiceMuted, startNewSession } =
    useInstantAssistantController();
  const pathname = usePathname();
  const assistantPrompts = useAssistantPrompts(namespaceId);
  const [feedbackByNamespace, setFeedbackByNamespace] = useState<Record<string, Record<string, boolean>>>({});
  const [contextOptOutPending, setContextOptOutPending] = useState(false);
  const [showContextWarning, setShowContextWarning] = useState(false);
  const [voiceEnablePending, setVoiceEnablePending] = useState(false);
  const [isResettingChat, setIsResettingChat] = useState(false);
  const feedbackCompleted = useMemo(
    () => (namespaceId ? feedbackByNamespace[namespaceId] ?? {} : {}),
    [feedbackByNamespace, namespaceId],
  );
  const routeContextCards = useMemo(
    () => buildRouteContextCards(namespaceId, pathname),
    [namespaceId, pathname],
  );
  useAssistantContextSource("route", routeContextCards);
  const hasAuthoritativeContext = useMemo(
    () => contextCards.some(isAuthoritativeContextCard),
    [contextCards],
  );

  const [controlsOpen, setControlsOpen] = useState(false);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const threadPanelRef = useRef<ChatThreadPanelHandle | null>(null);
  const anchoredToLatestRef = useRef(false);
  const [panelBounds, setPanelBounds] = useState<PanelBounds>(() => ({
    width: PANEL_DEFAULT_WIDTH,
    height: PANEL_DEFAULT_HEIGHT,
    x: PANEL_MARGIN,
    y: PANEL_MARGIN,
  }));
  const panelBoundsLoadedRef = useRef(false);
  const interactionRef = useRef<PanelInteraction | null>(null);
  // Drag/resize window listeners are torn down by aborting this controller, so
  // `endInteraction` doesn't have to name itself (or `handlePointerMove`) to
  // remove them and can't leak a listener if either identity changes mid-drag.
  const interactionListenersRef = useRef<AbortController | null>(null);

  const isBrowser = typeof window !== "undefined";
  const showEmptyState = messages.length === 0;
  const showComposerChips =
    messages.length > 0 &&
    messages.length <= MAX_COMPOSER_PROMPT_REPEATS &&
    assistantPrompts.length > 0;
  const events = useMemo<TaskThreadEvent[]>(
    () => mapMessagesToEvents(messages, feedbackCompleted, toolCalls),
    [messages, feedbackCompleted, toolCalls],
  );
  const workspaceBadge = namespaceId ? namespaceId.slice(0, 8).toUpperCase() : "NO WORKSPACE";
  const voiceConnected = voiceConnectionState === "connected";
  const voiceActive = voiceConnected;
  const voiceConnecting = voiceConnectionState === "connecting" || isVoiceConnecting;
  const shouldRenderPanel = featureEnabled && isBrowser && isPanelOpen;

  const clampBounds = useCallback(
    (bounds: PanelBounds): PanelBounds => {
      if (!isBrowser) {
        return bounds;
      }

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      if (viewportWidth <= PANEL_MARGIN * 2 || viewportHeight <= PANEL_MARGIN * 2) {
        return bounds;
      }
      const availableWidth = Math.max(1, viewportWidth - PANEL_MARGIN * 2);
      const availableHeight = Math.max(1, viewportHeight - PANEL_MARGIN * 2);
      const minWidth = Math.min(PANEL_MIN_WIDTH, availableWidth);
      const minHeight = Math.min(PANEL_MIN_HEIGHT, availableHeight);
      const width = Math.min(Math.max(bounds.width, minWidth), availableWidth);
      const height = Math.min(Math.max(bounds.height, minHeight), availableHeight);
      const maxX = Math.max(PANEL_MARGIN, viewportWidth - width - PANEL_MARGIN);
      const maxY = Math.max(PANEL_MARGIN, viewportHeight - height - PANEL_MARGIN);

      return {
        width,
        height,
        x: Math.min(Math.max(bounds.x, PANEL_MARGIN), maxX),
        y: Math.min(Math.max(bounds.y, PANEL_MARGIN), maxY),
      };
    },
    [isBrowser],
  );

  useEffect(() => {
    if (!isBrowser || panelBoundsLoadedRef.current) {
      return;
    }

    let frameId: number | null = null;

    const restoreBounds = () => {
      if (!isBrowser) {
        return;
      }

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const viewportReady = viewportWidth > PANEL_MARGIN * 2 && viewportHeight > PANEL_MARGIN * 2;
      if (!viewportReady) {
        frameId = window.requestAnimationFrame(restoreBounds);
        return;
      }

      panelBoundsLoadedRef.current = true;
      frameId = null;

      const raw = window.localStorage.getItem(PANEL_STORAGE_KEY);
      if (raw) {
        try {
          const parsed = JSON.parse(raw) as Partial<PanelBounds>;
          setPanelBounds((prev) => clampBounds({ ...prev, ...parsed }));
          return;
        } catch (error) {
          console.warn("Assistant panel bounds restore failed", error);
        }
      }

      setPanelBounds((prev) => {
        const maxWidth = viewportWidth - PANEL_MARGIN * 2;
        const maxHeight = viewportHeight - PANEL_MARGIN * 2;
        const width = Math.min(prev.width, maxWidth);
        const height = Math.min(prev.height, maxHeight);
        return clampBounds({
          width,
          height,
          x: viewportWidth - width - PANEL_MARGIN,
          y: viewportHeight - height - PANEL_MARGIN,
        });
      });
    };

    restoreBounds();

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [clampBounds, isBrowser]);

  useEffect(() => {
    if (!isBrowser || !panelBoundsLoadedRef.current) {
      return;
    }
    window.localStorage.setItem(PANEL_STORAGE_KEY, JSON.stringify(panelBounds));
  }, [isBrowser, panelBounds]);

  useEffect(() => {
    if (!isBrowser) {
      return;
    }

    const handleResize = () => {
      setPanelBounds((prev) => clampBounds(prev));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [clampBounds, isBrowser]);

  const setInteractionMode = useCallback((mode: "move" | "resize" | null) => {
    if (typeof document === "undefined") {
      return;
    }

    document.body.style.userSelect = mode ? "none" : "";
    if (mode === "move") {
      document.body.style.cursor = "grabbing";
    } else if (mode === "resize") {
      document.body.style.cursor = "nwse-resize";
    } else {
      document.body.style.cursor = "";
    }
  }, []);

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      if (!interactionRef.current) {
        return;
      }

      event.preventDefault();
      if (interactionRef.current.type === "move") {
        const { offsetX, offsetY } = interactionRef.current;
        setPanelBounds((prev) =>
          clampBounds({
            ...prev,
            x: event.clientX - offsetX,
            y: event.clientY - offsetY,
          }),
        );
      } else {
        const { startWidth, startHeight, startX, startY } = interactionRef.current;
        const nextWidth = startWidth + (event.clientX - startX);
        const nextHeight = startHeight + (event.clientY - startY);
        setPanelBounds((prev) =>
          clampBounds({
            ...prev,
            width: nextWidth,
            height: nextHeight,
          }),
        );
      }
    },
    [clampBounds],
  );

  const endInteraction = useCallback(() => {
    interactionRef.current = null;
    setInteractionMode(null);
    interactionListenersRef.current?.abort();
    interactionListenersRef.current = null;
  }, [setInteractionMode]);

  const beginMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!panelRef.current || !isBrowser || event.button !== 0) {
        return;
      }
      event.preventDefault();
      const rect = panelRef.current.getBoundingClientRect();
      interactionRef.current = {
        type: "move",
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
      };
      setInteractionMode("move");
      const listeners = new AbortController();
      interactionListenersRef.current = listeners;
      window.addEventListener("pointermove", handlePointerMove, { signal: listeners.signal });
      window.addEventListener("pointerup", endInteraction, { signal: listeners.signal });
    },
    [endInteraction, handlePointerMove, isBrowser, setInteractionMode],
  );

  const beginResize = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!panelRef.current || !isBrowser || event.button !== 0) {
        return;
      }
      event.preventDefault();
      const rect = panelRef.current.getBoundingClientRect();
      interactionRef.current = {
        type: "resize",
        startWidth: rect.width,
        startHeight: rect.height,
        startX: event.clientX,
        startY: event.clientY,
      };
      setInteractionMode("resize");
      const listeners = new AbortController();
      interactionListenersRef.current = listeners;
      window.addEventListener("pointermove", handlePointerMove, { signal: listeners.signal });
      window.addEventListener("pointerup", endInteraction, { signal: listeners.signal });
    },
    [endInteraction, handlePointerMove, isBrowser, setInteractionMode],
  );

  useEffect(() => {
    return () => {
      if (interactionRef.current) {
        endInteraction();
      }
    };
  }, [endInteraction]);

  const panelStyle = useMemo(
    () => ({
      width: panelBounds.width,
      height: panelBounds.height,
      left: panelBounds.x,
      top: panelBounds.y,
    }),
    [panelBounds],
  );

  useEffect(() => {
    if (!isPanelOpen) {
      anchoredToLatestRef.current = false;
    }
  }, [isPanelOpen]);

  useEffect(() => {
    anchoredToLatestRef.current = false;
  }, [namespaceId]);

  useEffect(() => {
    if (!featureEnabled || voiceFeatureEnabled || !namespaceId) {
      return;
    }

    if (loadVoiceOptIn(namespaceId)) {
      actions.setVoiceFeatureEnabled(true);
    }
  }, [actions, featureEnabled, namespaceId, voiceFeatureEnabled]);

  const handleSystemAction = useCallback(
    async (_eventId: string, actionId: string) => {
      const [, action, messageId] = actionId.split(":");
      if (!messageId) {
        return;
      }

      try {
        if (action === "thumbs_up") {
          await submitFeedback(messageId, "ThumbsUp");
        } else if (action === "thumbs_down") {
          await submitFeedback(messageId, "ThumbsDown");
        } else if (action === "flag") {
          let notes: string | undefined;
          if (typeof window !== "undefined") {
            const detail = window.prompt("Flag inaccuracy", "Describe what should change");
            notes = detail?.trim() || undefined;
          }
          await submitFeedback(messageId, "FlagInaccuracy", notes);
        }

        setFeedbackByNamespace((prev) => {
          if (!namespaceId) {
            return prev;
          }
          const existing = prev[namespaceId] ?? {};
          return {
            ...prev,
            [namespaceId]: {
              ...existing,
              [messageId]: true,
            },
          };
        });
      } catch (error) {
        console.warn("Assistant feedback failed", error);
        actions.setError((error as Error)?.message ?? "Unable to record feedback.");
      }
    },
    [actions, namespaceId, submitFeedback],
  );

  useEffect(() => {
    if (!isPanelOpen) {
      return;
    }

    composerRef.current?.focus({ preventScroll: true });
  }, [isPanelOpen]);

  useEffect(() => {
    if (!isPanelOpen || anchoredToLatestRef.current) {
      return;
    }

    const latestEventId = events[events.length - 1]?.id;
    if (!latestEventId) {
      return;
    }

    const scrolled = threadPanelRef.current?.scrollToEvent(latestEventId, { behavior: "auto" });
    if (scrolled) {
      anchoredToLatestRef.current = true;
    }
  }, [isPanelOpen, events]);

  useEffect(() => {
    if (!isPanelOpen || !isBrowser) {
      return undefined;
    }

    const handlePointer = (event: MouseEvent | TouchEvent) => {
      if (!panelRef.current) {
        return;
      }

      if (panelRef.current.contains(event.target as Node)) {
        return;
      }

      actions.closePanel();
    };

    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("touchstart", handlePointer);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("touchstart", handlePointer);
    };
  }, [isPanelOpen, isBrowser, actions]);

  useEffect(() => {
    if (!isPanelOpen) {
      return;
    }
    composerRef.current?.blur();
  }, [focusReleaseSignal, isPanelOpen]);

  useEffect(() => {
    if (!isPanelOpen || !isBrowser) {
      actions.setHasSelection(false);
      return undefined;
    }

    const handleSelectionChange = () => {
      const selectionText = window.getSelection()?.toString().trim();
      actions.setHasSelection(Boolean(selectionText));
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    handleSelectionChange();

    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
      actions.setHasSelection(false);
    };
  }, [isPanelOpen, isBrowser, actions]);

  useEffect(() => {
    if (!hasAuthoritativeContext || typeof window === "undefined") {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      setContextOptOutPending(false);
      setShowContextWarning(false);
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [hasAuthoritativeContext]);

  const handleVoiceToggle = useCallback(() => {
    if (!voiceFeatureEnabled) {
      return;
    }

    const run = async () => {
      try {
        if (voiceConnectionState === "connected" || voiceConnectionState === "connecting") {
          await endVoiceSession("user_toggle");
        } else {
          await startVoiceSession();
        }
      } catch (error) {
        console.warn("Assistant voice bootstrap failed", error);
      }
    };

    void run();
  }, [voiceFeatureEnabled, voiceConnectionState, startVoiceSession, endVoiceSession]);

  const handleVoiceMuteToggle = useCallback(() => {
    setVoiceMuted(!voiceMuted);
  }, [setVoiceMuted, voiceMuted]);

  const handleNewChat = useCallback(() => {
    if (isResettingChat) {
      return;
    }

    if (!namespaceId) {
      actions.setError("Select a workspace before starting a new chat.");
      return;
    }

    if (!isAuthenticated) {
      actions.setError("Sign in before starting a new chat.");
      return;
    }

    setIsResettingChat(true);
    actions.setError(null);

    void startNewSession()
      .catch((error) => {
        console.warn("Instant assistant new chat reset failed", error);
      })
      .finally(() => {
        setIsResettingChat(false);
      });
  }, [actions, namespaceId, startNewSession, isResettingChat, isAuthenticated]);

  const attemptSend = useCallback(
    async (forceOptOut = false) => {
      const payload = composerDraft.trim();
      if (!payload) {
        return;
      }

      const optedOut = contextOptOutPending || forceOptOut;
      const contextAllowed = hasAuthoritativeContext || optedOut;
      if (!contextAllowed) {
        setShowContextWarning(true);
        setControlsOpen(true);
        return;
      }

      setShowContextWarning(false);
      if (forceOptOut && !contextOptOutPending) {
        setContextOptOutPending(true);
      }

      try {
        await sendPrompt(payload, { contextOptOut: optedOut });
        actions.setComposerDraft("");
        setContextOptOutPending(false);
      } catch (error) {
        console.warn("Assistant send failed", error);
      }
    },
    [composerDraft, hasAuthoritativeContext, contextOptOutPending, sendPrompt, actions],
  );

  const handleSend = useCallback(() => {
    void attemptSend();
  }, [attemptSend]);

  const handleSendWithoutContext = useCallback(() => {
    void attemptSend(true);
  }, [attemptSend]);

  const handleOverlayDismiss = useCallback(() => {
    actions.setDiscoveryUi({ showOverlay: false, showGlow: false, showTooltip: false });
  }, [actions]);

  const handleOverlayOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleOverlayDismiss();
      }
    },
    [handleOverlayDismiss],
  );

  const handleVoiceFeatureEnable = useCallback(() => {
    if (voiceEnablePending) {
      return;
    }

    setVoiceEnablePending(true);

    const run = async () => {
      actions.setVoiceFeatureEnabled(true);
      if (namespaceId) {
        persistVoiceOptIn(namespaceId, true);
      }

      try {
        await startVoiceSession();
      } catch (error) {
        console.warn("Instant assistant voice enable failed", error);
      } finally {
        setVoiceEnablePending(false);
      }
    };

    void run();
  }, [actions, namespaceId, startVoiceSession, voiceEnablePending]);

  const handleVoiceFeatureDisable = useCallback(() => {
    if (!voiceFeatureEnabled) {
      return;
    }

    const run = async () => {
      try {
        if (voiceSession) {
          await endVoiceSession("user_hangup");
        }
      } catch (error) {
        console.warn("Instant assistant voice disable failed", error);
      } finally {
        actions.resetVoiceConnection();
        actions.setVoiceFeatureEnabled(false);
        if (namespaceId) {
          persistVoiceOptIn(namespaceId, false);
        }
      }
    };

    void run();
  }, [actions, endVoiceSession, namespaceId, voiceFeatureEnabled, voiceSession]);

  if (!shouldRenderPanel) {
    return null;
  }

  const panel = (
    <div className="fixed inset-0 z-40 pointer-events-none">
      <section
        ref={panelRef}
        role="dialog"
        aria-label="Ask Scirix assistant"
        className="pointer-events-auto absolute flex min-h-0 flex-col overflow-hidden rounded-[var(--radius-xxl)] border border-border/70 bg-[var(--token-foundation-surface-base)]/95 p-4 text-foreground shadow-2xl backdrop-blur-xl"
        style={panelStyle}
      >
        <header className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Ask Scirix</h2>
              <Badge variant="secondary" className="text-[11px] uppercase tracking-[0.24em]">
                {workspaceBadge}
              </Badge>
            </div>
            <p className="mt-1 text-[12px] text-muted-foreground">Space to open · Esc to close</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/60 text-muted-foreground transition hover:text-foreground cursor-grab active:cursor-grabbing"
              aria-label="Drag assistant panel"
              onPointerDown={beginMove}
            >
              <Move className="h-4 w-4" aria-hidden />
            </button>
            <AssistantControlsDropdown
              open={controlsOpen}
              onOpenChange={setControlsOpen}
              voiceFeatureEnabled={voiceFeatureEnabled}
              voiceEnablePending={voiceEnablePending}
              voiceSession={voiceSession}
              voiceConnectionState={voiceConnectionState}
              voiceLevel={voiceLevel}
              voiceMuted={voiceMuted}
              voiceStartedAt={voiceStartedAt}
              voiceError={voiceError}
              onStartVoice={() => {
                void startVoiceSession();
              }}
              onEndVoice={(reason) => {
                void endVoiceSession(reason ?? "user_hangup");
              }}
              onToggleMute={handleVoiceMuteToggle}
              onEnableVoice={handleVoiceFeatureEnable}
              onDisableVoice={handleVoiceFeatureDisable}
              contextCards={contextCards}
              hasSelection={hasSelection}
              onAttachSelection={() => actions.captureSelectionContext()}
              onRemoveContextCard={(sourceId) => actions.removeContextCard(sourceId)}
              hasAuthoritativeContext={hasAuthoritativeContext}
              showContextWarning={showContextWarning}
              onSendWithoutContext={() => {
                setControlsOpen(false);
                handleSendWithoutContext();
              }}
            />
            <button
              type="button"
              className="inline-flex h-9 items-center gap-1.5 rounded-full border border-border/60 px-3 text-xs font-semibold text-muted-foreground transition hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="Start a new chat session"
              onClick={handleNewChat}
              disabled={!namespaceId || isResettingChat || !isAuthenticated}
              title={!isAuthenticated ? "Sign in to start a new chat" : undefined}
              aria-busy={isResettingChat}
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              {isResettingChat ? "Resetting…" : "New chat"}
            </button>
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/60 text-muted-foreground transition hover:text-foreground"
              aria-label="Replay onboarding"
              onClick={() =>
                actions.setDiscoveryUi(
                  { showOverlay: true, showGlow: true, showTooltip: true },
                  { resetGlowTimer: true },
                )
              }
            >
              <HelpCircle className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/60 text-muted-foreground transition hover:text-foreground"
              aria-label="Close assistant"
              onClick={actions.closePanel}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        <StatusBanner status={status} />
        {showContextWarning ? (
          <p className="mt-2 rounded-[var(--radius-lg)] border border-[color:var(--token-accent-warning-border-strong)] bg-[color:var(--token-accent-warning-surface)] px-3 py-2 text-xs text-[color:var(--token-accent-warning-strong)]">
            Attach workspace context or open Assistant controls to send without context.
          </p>
        ) : null}

        <div className="mt-4 flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
          {showEmptyState ? (
            <AssistantEmptyState
              prompts={assistantPrompts}
              onPrompt={(prompt) => actions.setComposerDraft(prompt)}
            />
          ) : null}

          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
            <ChatThreadPanel
              ref={threadPanelRef}
              events={events}
              isLoading={false}
              onLoadEarlier={() => {}}
              onSystemAction={handleSystemAction}
              hideEmptyStateMessage
              composer={{
                value: composerDraft,
                onChange: actions.setComposerDraft,
                onSend: handleSend,
                isSending,
                voiceEnabled: voiceFeatureEnabled,
                voiceActive,
                voiceConnecting,
                onToggleVoice: handleVoiceToggle,
                attachments: [],
                onSelectFiles: () => {},
                onRemoveAttachment: () => {},
                placeholder: "What can I help you with?",
                sendLabel: "Send",
                showAttachmentButton: false,
                showVoiceButton: voiceFeatureEnabled,
                showMentionButton: false,
              }}
              composerRef={composerRef}
            />

            {showComposerChips ? (
              <StarterPromptChips
                prompts={assistantPrompts}
                onSelect={(prompt) => actions.setComposerDraft(prompt)}
              />
            ) : null}

            {queue ? <QueueState queue={queue} /> : null}
            {errorMessage ? <InlineError message={errorMessage} onDismiss={() => actions.setError(null)} /> : null}
          </div>
        </div>

        {discoveryUi.showOverlay ? (
          <Dialog open={discoveryUi.showOverlay} onOpenChange={handleOverlayOpenChange}>
            <DialogContent size="sm" className="space-y-4 text-center">
              <DialogHeader className="space-y-2 text-center">
                <DialogTitle className="text-2xl">Ask Scirix</DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground">
                  Ask questions about your project, get summaries, and draft better tasks — right here.
                </DialogDescription>
              </DialogHeader>
              <Button className="w-full" onClick={handleOverlayDismiss} autoFocus>
                Got it
              </Button>
            </DialogContent>
          </Dialog>
        ) : null}

        <button
          type="button"
          aria-label="Resize assistant panel"
          className="absolute bottom-2 right-2 flex h-6 w-6 items-center justify-center rounded-full border border-border/40 bg-background/30 text-muted-foreground/70 transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-se-resize"
          onPointerDown={beginResize}
        >
          <CornerDownRight className="h-4 w-4" aria-hidden />
        </button>
      </section>
    </div>
  );

  return createPortal(panel, document.body);
}

function AssistantControlsDropdown({
  open,
  onOpenChange,
  voiceFeatureEnabled,
  voiceEnablePending,
  voiceSession,
  voiceConnectionState,
  voiceLevel,
  voiceMuted,
  voiceStartedAt,
  voiceError,
  onStartVoice,
  onEndVoice,
  onToggleMute,
  onEnableVoice,
  onDisableVoice,
  contextCards,
  hasSelection,
  onAttachSelection,
  onRemoveContextCard,
  hasAuthoritativeContext,
  showContextWarning,
  onSendWithoutContext,
}: {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  voiceFeatureEnabled: boolean;
  voiceEnablePending: boolean;
  voiceSession: InstantAssistantVoiceSessionResponse | null;
  voiceConnectionState: VoiceConnectionState;
  voiceLevel: number;
  voiceMuted: boolean;
  voiceStartedAt: string | null;
  voiceError: string | null;
  onStartVoice: () => void;
  onEndVoice: (reason?: string) => void;
  onToggleMute: () => void;
  onEnableVoice: () => void;
  onDisableVoice: () => void;
  contextCards: InstantAssistantContextCard[];
  hasSelection: boolean;
  onAttachSelection: () => void;
  onRemoveContextCard: (sourceId: string) => void;
  hasAuthoritativeContext: boolean;
  showContextWarning: boolean;
  onSendWithoutContext: () => void;
}) {
  const needsAttention = showContextWarning || !hasAuthoritativeContext || !voiceFeatureEnabled;
  const controlButtonClass = [
    "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition",
    "hover:border-foreground/70",
    needsAttention
      ? "border-[color:var(--token-accent-warning-core)] text-foreground"
      : "border-border/60 text-muted-foreground hover:text-foreground",
  ].join(" ");

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange} modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={controlButtonClass}
          aria-label="Assistant controls"
          aria-expanded={open}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
          Assistant controls
          {needsAttention ? <span className="h-2 w-2 rounded-full bg-[color:var(--token-accent-warning-core)]" aria-hidden /> : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-[380px] max-h-[75vh] overflow-y-auto p-0"
      >
        <div className="space-y-4 p-3">
          <div className="space-y-2">
            <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-[0.32em] text-muted-foreground/80">
              Voice
            </DropdownMenuLabel>
            {voiceFeatureEnabled ? (
              <VoiceSessionBanner
                enabled={voiceFeatureEnabled}
                session={voiceSession}
                connectionState={voiceConnectionState}
                level={voiceLevel}
                isMuted={voiceMuted}
                startedAt={voiceStartedAt}
                errorMessage={voiceError}
                onStartVoice={onStartVoice}
                onEndVoice={onEndVoice}
                onToggleMute={onToggleMute}
                onDisableVoice={onDisableVoice}
              />
            ) : (
              <VoiceEnablePrompt onEnable={onEnableVoice} isPending={voiceEnablePending} />
            )}
          </div>
          <DropdownMenuSeparator />
          <div className="space-y-3">
            <ContextSection
              cards={contextCards}
              onRemove={onRemoveContextCard}
              showEmptyPlaceholder
            />
            {hasSelection ? <AttachSelectionChip onAttach={onAttachSelection} /> : null}
            {!hasAuthoritativeContext ? (
              <ContextMissingBanner
                showWarning={showContextWarning}
                onSendWithoutContext={onSendWithoutContext}
              />
            ) : null}
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function VoiceSessionBanner({
  enabled,
  session,
  connectionState,
  level,
  isMuted,
  startedAt,
  errorMessage,
  onStartVoice,
  onEndVoice,
  onToggleMute,
  onDisableVoice,
}: {
  enabled: boolean;
  session: InstantAssistantVoiceSessionResponse | null;
  connectionState: VoiceConnectionState;
  level: number;
  isMuted: boolean;
  startedAt: string | null;
  errorMessage: string | null;
  onStartVoice: () => void;
  onEndVoice: (reason?: string) => void;
  onToggleMute: () => void;
  onDisableVoice: () => void;
}) {
  const connected = connectionState === "connected";
  const connecting = connectionState === "connecting";
  const hasSession = Boolean(session);
  const providerSummary = session
    ? `${session.session.provider.type} • ${session.session.provider.model}`
    : "Browser voice coming online soon";
  const expiresLabel = session ? formatVoiceExpiry(session.session.expiresAt) : null;
  const personaVoice = session?.session.persona.voiceId;
  const [elapsedTick, setElapsedTick] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled || !startedAt || !connected) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setElapsedTick(Date.now());
    }, 1000);

    return () => window.clearInterval(interval);
  }, [enabled, startedAt, connected]);

  const elapsedLabel = useMemo(() => {
    if (!enabled || !startedAt || !connected) {
      return null;
    }
    const elapsed = elapsedTick - Date.parse(startedAt);
    return formatElapsedDuration(elapsed);
  }, [enabled, startedAt, connected, elapsedTick]);

  if (!enabled) {
    return null;
  }

  const subtitle = connected
    ? elapsedLabel ?? "Voice session live"
    : expiresLabel
      ? `Expires ${expiresLabel}`
      : providerSummary;

  const handlePrimary = () => {
    if (connected || connecting) {
      onEndVoice(connected ? "user_hangup" : "user_cancelled");
      return;
    }
    onStartVoice();
  };

  const primaryLabel = connecting
    ? "Cancel"
    : connected
      ? "Hang up"
      : hasSession
        ? "Refresh"
        : "Enable voice";

  const meterWidth = Math.max(4, Math.min(100, Math.round(level * 100)));
  const meterClass = isMuted
    ? "h-1.5 rounded-full bg-muted-foreground/50 transition-[width]"
    : "h-1.5 rounded-full bg-[color:var(--token-accent-success-muted)] transition-[width]";

  return (
    <div className="mt-3 rounded-[var(--radius-lg)] border border-border/60 bg-muted/20 px-3 py-3 text-sm">
      <div className="flex items-start gap-3">
        <div className="flex flex-1 flex-col">
          <span className="flex items-center gap-2 font-semibold text-foreground">
            <Mic className="h-4 w-4" aria-hidden />
            {connected ? "Voice session live" : connecting ? "Starting voice session…" : hasSession ? "Voice session ready" : "Voice disabled"}
          </span>
          <span className="text-xs text-muted-foreground">{subtitle}</span>
          {personaVoice ? (
            <span className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground/80">Voice {personaVoice}</span>
          ) : null}
          {connected ? (
            <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
              <span>{isMuted ? "Muted" : "Mic live"}</span>
              <div className="h-1.5 flex-1 rounded-full bg-muted/40">
                <div className={meterClass} style={{ width: `${meterWidth}%` }} />
              </div>
            </div>
          ) : null}
          {errorMessage ? <p className="mt-2 text-xs text-destructive">{errorMessage}</p> : null}
        </div>
        <div className="flex flex-col items-end gap-2">
          <Button
            variant="outline"
            size="sm"
            className="rounded-full px-4"
            onClick={handlePrimary}
            disabled={connecting && !hasSession}
          >
            {primaryLabel}
          </Button>
          {connected ? (
            <Button variant="ghost" size="sm" className="rounded-full px-4" onClick={onToggleMute}>
              {isMuted ? "Unmute" : "Mute"}
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full px-4 text-xs text-muted-foreground"
            onClick={onDisableVoice}
          >
            Turn off voice UI
          </Button>
        </div>
      </div>
    </div>
  );
}

function VoiceEnablePrompt({ onEnable, isPending = false }: { onEnable: () => void; isPending?: boolean }) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-4 rounded-[var(--radius-lg)] border border-dashed border-border/60 bg-muted/20 px-3 py-3 text-sm">
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-center gap-2 font-semibold text-foreground">
          <Mic className="h-4 w-4" aria-hidden />
          Talk to Scirix
        </span>
        <span className="text-xs text-muted-foreground">
          Enable the voice UI to start live conversations without leaving this workspace.
        </span>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="inline-flex items-center gap-2 rounded-full px-4"
        onClick={onEnable}
        disabled={isPending}
        aria-busy={isPending}
      >
        <span className="inline-flex items-center gap-2">
          {isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Mic className="h-4 w-4" aria-hidden />
          )}
          {isPending ? "Enabling…" : "Enable voice UI"}
        </span>
      </Button>
    </div>
  );
}


function formatVoiceExpiry(timestamp: string) {
  const parsed = Date.parse(timestamp);
  if (Number.isNaN(parsed)) {
    return timestamp;
  }

  return new Date(parsed).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatElapsedDuration(milliseconds: number | null) {
  if (!milliseconds || !Number.isFinite(milliseconds) || milliseconds <= 0) {
    return null;
  }

  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `Live for ${hours}h ${mins}m`;
  }

  const paddedSeconds = seconds.toString().padStart(2, "0");
  return `Live for ${minutes}:${paddedSeconds}`;
}

function ContextSection({
  cards,
  onRemove,
  showEmptyPlaceholder = false,
}: {
  cards: InstantAssistantContextCard[];
  onRemove: (sourceId: string) => void;
  showEmptyPlaceholder?: boolean;
}) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-muted-foreground/80">Context</p>
      <div className="mt-2 space-y-2">
        {cards.length ? (
          cards.map((card) => (
            <ContextCard key={card.sourceId ?? `${card.kind}:${card.title}`} card={card} onRemove={onRemove} />
          ))
        ) : showEmptyPlaceholder ? (
          <p className="text-xs text-muted-foreground">No context attached yet.</p>
        ) : null}
      </div>
    </div>
  );
}

function ContextCard({
  card,
  onRemove,
}: {
  card: InstantAssistantContextCard;
  onRemove: (sourceId: string) => void;
}) {
  return (
    <div className="flex items-start gap-3 rounded-[var(--radius-lg)] border border-border/60 bg-background/80 px-3 py-2 text-sm">
      <div className="flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-muted-foreground/80">
          {card.title ?? "Context"}
        </p>
        {card.subtitle ? <p className="mt-0.5 truncate text-foreground">{card.subtitle}</p> : null}
      </div>
      {card.sourceId ? (
        <button
          type="button"
          className="text-[10px] font-semibold uppercase tracking-[0.32em] text-muted-foreground transition hover:text-foreground"
          onClick={() => onRemove(card.sourceId!)}
        >
          Remove
        </button>
      ) : null}
    </div>
  );
}

function AttachSelectionChip({ onAttach }: { onAttach: () => void }) {
  return (
    <div className="flex justify-start">
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/80 px-3 py-1 text-xs font-medium text-foreground transition hover:border-foreground/70 hover:text-foreground"
        onClick={onAttach}
      >
        Attach current selection
      </button>
    </div>
  );
}

function StarterPromptChips({ prompts, onSelect }: { prompts: string[]; onSelect: (prompt: string) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {prompts.map((prompt) => (
        <button
          key={prompt}
          type="button"
          className="rounded-full border border-border/60 px-3 py-1 text-sm text-muted-foreground transition hover:border-foreground/70 hover:text-foreground"
          onClick={() => onSelect(prompt)}
        >
          {prompt}
        </button>
      ))}
    </div>
  );
}

function AssistantEmptyState({ prompts, onPrompt }: { prompts: string[]; onPrompt: (value: string) => void }) {
  return (
    <div className="rounded-[var(--radius-xl)] bg-muted/20 px-4 py-5 text-center">
      <p className="text-sm font-medium text-muted-foreground">What can I help you with?</p>
      {prompts.length ? (
        <div className="mt-3 flex flex-wrap justify-center gap-2">
          {prompts.map((prompt) => (
            <button
              key={prompt}
              type="button"
              className="rounded-full border border-border/60 px-3 py-1 text-xs text-muted-foreground transition hover:border-foreground/70 hover:text-foreground"
              onClick={() => onPrompt(prompt)}
            >
              {prompt}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function StatusBanner({ status }: { status: InstantAssistantStatusEvent }) {
  const [remainingMs, setRemainingMs] = useState<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || status.kind !== "queued" || !status.availableAtUtc) {
      return;
    }

    const target = new Date(status.availableAtUtc).getTime();
    if (Number.isNaN(target)) {
      return;
    }

    const update = () => {
      setRemainingMs(Math.max(0, target - Date.now()));
    };

    update();
    const id = window.setInterval(update, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, [status.kind, status.availableAtUtc]);

  if (status.kind === "idle") {
    return null;
  }

  let message = status.detail ?? "";
  if (!message) {
    if (status.kind === "thinking") {
      message = "Scirix is thinking…";
    } else if (status.kind === "slow_path") {
      message = "Taking longer than usual…";
    } else if (status.kind === "queued") {
      if (remainingMs !== null) {
        const seconds = Math.max(1, Math.ceil(remainingMs / 1000));
        message = `Queued – ready in ${seconds}s`;
      } else {
        message = "Queued";
      }
    } else if (status.kind === "error") {
      message = "Assistant ran into a problem.";
    }
  }

  if (!message) {
    return null;
  }

  const contextLabel = formatContextSummary(status.contextSummary);

  return (
    <div className="mt-3 flex items-start gap-2 rounded-[var(--radius-lg)] border border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-foreground" aria-hidden />
      <div className="flex flex-col gap-1">
        <span>{message}</span>
        {contextLabel ? <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground/80">{contextLabel}</span> : null}
      </div>
    </div>
  );
}

function formatContextSummary(summary?: InstantAssistantContextSummary | null) {
  if (!summary || summary.totalCards === 0) {
    return null;
  }

  const typeLabel = summary.types.length ? summary.types.map((type) => type.replace(/_/g, " ")).join(" + ") : `${summary.totalCards} card${summary.totalCards === 1 ? "" : "s"}`;
  let hydrationLabel = "";
  switch (summary.hydrationStatus) {
    case "pending":
      hydrationLabel = " (hydrating)";
      break;
    case "partial":
      hydrationLabel = " (partial)";
      break;
    case "failed":
      hydrationLabel = summary.failureReason ? ` (failed: ${summary.failureReason})` : " (failed)";
      break;
    case "success":
      hydrationLabel = " (ready)";
      break;
    default:
      break;
  }

  return `Context: ${typeLabel}${hydrationLabel}`;
}

function QueueState({ queue }: { queue: InstantAssistantQueueState }) {
  if (!queue.depth && !queue.nextAvailableInMs) {
    return null;
  }

  const waitSeconds = queue.nextAvailableInMs ? Math.ceil(queue.nextAvailableInMs / 1000) : null;

  return (
    <div className="flex items-center gap-2 rounded-[var(--radius-lg)] border border-dashed border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
      <span className="font-semibold text-foreground">Queue</span>
      <span>{queue.depth} waiting</span>
      {waitSeconds ? <span>· Next slot in {waitSeconds}s</span> : null}
    </div>
  );
}

function InlineError({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div className="rounded-[var(--radius-lg)] border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
      <div className="flex items-center justify-between gap-2">
        <span>{message}</span>
        <button
          type="button"
          className="text-[10px] font-semibold uppercase tracking-[0.32em]"
          onClick={onDismiss}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

function ContextMissingBanner({
  showWarning,
  onSendWithoutContext,
}: {
  showWarning: boolean;
  onSendWithoutContext: () => void;
}) {
  const borderClass = showWarning
    ? "border-[color:var(--token-accent-warning-core)] bg-[color:var(--token-accent-warning-surface)]"
    : "border-border/60 bg-muted/20";

  return (
    <div className={`flex flex-col gap-3 rounded-[var(--radius-lg)] border px-3 py-2 text-sm ${borderClass}`}>
      <div>
        <p className="font-semibold text-foreground">Attach workspace context</p>
        <p className="text-xs text-muted-foreground">
          Pin the task, document, or file you are working on so Scirix can cite it. You can still continue without
          context if needed.
        </p>
      </div>
      <div>
        <Button type="button" size="sm" variant="outline" onClick={onSendWithoutContext} className="rounded-full">
          Send without context
        </Button>
      </div>
    </div>
  );
}

function mapMessagesToEvents(
  messages: InstantAssistantMessage[],
  feedbackCompleted: Record<string, boolean>,
  toolCalls: InstantAssistantToolCallEvent[],
): TaskThreadEvent[] {
  const events: TaskThreadEvent[] = [];

  for (const message of messages) {
    const event: UserCommentEvent = {
      type: "user_comment",
      id: message.messageId,
      timestamp: new Date(message.createdAtUtc).getTime(),
      author: message.role === "assistant" ? "Scirix" : message.role === "system" ? "System" : "You",
      authorRole: message.role === "assistant" ? "system" : message.role === "system" ? "system" : "operator",
      avatarInitials: message.role === "assistant" ? "ME" : message.role === "system" ? "SYS" : "OP",
      content: message.content,
    };

    if (message.role === "assistant") {
      const messageId = message.messageId;
      const isCompleted = Boolean(feedbackCompleted[messageId]);
      event.inlineAction = {
        id: `feedback-${messageId}`,
        summary: "Feedback",
        status: isCompleted ? "completed" : "open",
        resultSummary: isCompleted ? "Thanks for the feedback." : undefined,
        actions: isCompleted
          ? undefined
          : [
              { id: `feedback:thumbs_up:${messageId}`, label: "Thumbs up", tone: "primary" },
              { id: `feedback:thumbs_down:${messageId}`, label: "Thumbs down" },
              { id: `feedback:flag:${messageId}`, label: "Flag inaccuracy", tone: "danger" },
            ],
      };
    }

    events.push(event);
  }

  for (const toolCall of toolCalls) {
    events.push(toolCallToPulseEvent(toolCall));
  }

  // Stable interleave by timestamp; ties keep insertion order so a tool call
  // emitted right before its assistant reply still renders above the reply.
  const decorated = events.map((event, index) => ({ event, index, ts: eventTimestamp(event) }));
  decorated.sort((a, b) => (a.ts - b.ts) || (a.index - b.index));
  return decorated.map((d) => d.event);
}

function eventTimestamp(event: TaskThreadEvent): number {
  if (event.type === "task_pulse") {
    return event.entries[0]?.timestamp ?? 0;
  }
  return event.timestamp;
}

const VALID_PULSE_KINDS = new Set<TaskPulseEntryKind>([
  "start", "reading", "planning", "preparing", "executing", "finalizing",
  "completed", "failed", "canceled", "paused", "syncing", "publishing",
  "commit", "action", "step_pending", "step_active", "step_completed",
  "querying_model", "reading_file", "writing_file", "editing_file",
  "browsing_files", "deleting_file", "creating_directory", "moving_file",
  "writing_skill", "using_skill", "deleting_skill", "listing_skills",
  "fetching_url", "googling", "creating_cron", "updating_cron",
  "listing_cron", "removing_cron", "completing_cron",
]);

function toolCallToPulseEvent(toolCall: InstantAssistantToolCallEvent): TaskPulseEvent {
  const timestamp = new Date(toolCall.timestampUtc).getTime();
  const candidateKind = toolCall.kind as TaskPulseEntryKind;
  const entryKind: TaskPulseEntryKind = VALID_PULSE_KINDS.has(candidateKind) ? candidateKind : "action";
  const summary = toolCall.ok
    ? toolCall.label
    : `${toolCall.label} (failed)`;
  const detail = !toolCall.ok && toolCall.errorMessage
    ? toolCall.errorMessage
    : toolCall.target ?? undefined;

  const entry: TaskPulseEntry = {
    id: toolCall.eventId,
    timestamp,
    stage: toolCall.ok ? "executing" : "error",
    kind: entryKind,
    summary,
    detail,
  };

  return {
    type: "task_pulse",
    id: `tool-call-${toolCall.eventId}`,
    taskId: toolCall.sessionId,
    taskLabel: toolCall.label,
    entries: [entry],
    collapsedByDefault: false,
  };
}

function buildRouteContextCards(namespaceId: string | null, pathname: string) {
  if (!namespaceId || !pathname) {
    return [];
  }

  const label = describeRoute(pathname);
  const sourceId = `route:${pathname}`;
  return [
    {
      kind: "route",
      title: label,
      subtitle: pathname,
      sourceId,
      metadata: {
        source: "auto",
        autoSourceId: sourceId,
      },
    } satisfies InstantAssistantContextCard,
  ];
}

function describeRoute(pathname: string) {
  if (pathname.startsWith("/tasks")) {
    return "Tasks workspace";
  }
  if (pathname.startsWith("/repository")) {
    return "Repository workspace";
  }
  if (pathname.startsWith("/documents")) {
    return "Documents workspace";
  }
  if (pathname.startsWith("/files")) {
    return "Files workspace";
  }
  return "Current view";
}

function isAuthoritativeContextCard(card: InstantAssistantContextCard) {
  return card.kind !== "route";
}
